// Basics lifted from https://github.com/tj/react-click-outside/

import {
  FloatingNode,
  FloatingTree,
  useFloating,
  useFloatingNodeId,
  useFloatingParentNodeId,
  useFloatingTree,
} from '@floating-ui/react';
import { useRefFacade } from '@timelyapp/react';
import React, { useEffect } from 'react';
import { getFloatingChildren } from './popovers/utils';

interface Props extends React.HTMLProps<HTMLDivElement> {
  onClickOutside: (event: MouseEvent | React.MouseEvent) => void;
  children: React.ReactNode;
  enabled?: boolean;
  forwardedRef?: React.RefObject<HTMLDivElement>;
}

const ClickOutsideTrap: React.FC<Props> = ({
  children,
  onClickOutside,
  enabled = true,
  forwardedRef,
  ...rest
}) => {
  const ref = useRefFacade(forwardedRef);
  const nodeId = useFloatingNodeId();
  const tree = useFloatingTree();
  useFloating({ nodeId, open: enabled });

  useEffect(() => {
    if (!enabled) return;

    const handleClickOutside = (event: MouseEvent | React.MouseEvent) => {
      const target = event.target instanceof HTMLElement ? event.target : null;
      if (!target) {
        return;
      }

      const ignoredParent = target.closest('.ignore-react-onclickoutside');
      if (ignoredParent) {
        return;
      }

      const targetIsInsideChildren =
        tree &&
        getFloatingChildren(tree.nodesRef.current, nodeId).some(node =>
          node.context?.refs.floating.current?.contains(target)
        );

      const isOutside = !ref.current?.contains(target) && !targetIsInsideChildren;
      if (isOutside) {
        onClickOutside(event);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [enabled, nodeId, onClickOutside, ref, tree]);

  return (
    <FloatingNode id={nodeId}>
      <div {...rest} ref={ref}>
        {children}
      </div>
    </FloatingNode>
  );
};

const ClickOutsideTrapWrapper: React.FC<Props> = props => {
  const parentNodeId = useFloatingParentNodeId();
  const node = <ClickOutsideTrap {...props} />;
  return !parentNodeId ? <FloatingTree>{node}</FloatingTree> : node;
};

export default ClickOutsideTrapWrapper;
